import { useState } from "react";

import { Button } from "@/components/button";

import type { GroupedLogs } from "../../utils/message-parsers";

interface FeedbackProps {
  data: GroupedLogs;
  onFeedbackClick?: (id: string) => void;
}

const INITIAL_VISIBLE_ITEMS = 3;

export function Feedback({ data, onFeedbackClick }: FeedbackProps) {
  const [showAll, setShowAll] = useState(false);
  const [isExpanded, setIsExpanded] = useState<string[]>([]);

  const feedbackItems = data.items.slice(1);
  const mainComment = data.items[0];

  const visibleItems = showAll
    ? feedbackItems
    : feedbackItems.slice(0, INITIAL_VISIBLE_ITEMS);
  const hiddenItems = feedbackItems.slice(INITIAL_VISIBLE_ITEMS);
  const hasMoreItems = feedbackItems.length > INITIAL_VISIBLE_ITEMS;

  const attachmentItem = data.attachment;
  const attachmentName = attachmentItem?.name || "attachment";
  const attachmentUrl = attachmentItem?.url || " ";

  const isImage = attachmentName
    .toLowerCase()
    .match(/(jpg|jpeg|png|gif|webp)$/i);

  const onClickShowMoreContent = (id: string) => {
    setIsExpanded((lastId) =>
      lastId.includes(id) ? lastId.filter((id) => id !== id) : [...lastId, id],
    );
  };

  return (
    <div className="rounded-xl flex items-start">
      <div className="flex flex-col items-center mr-4">
        {isImage ? (
          <img
            alt={attachmentName}
            className="w-[94px] h-[94px] object-cover rounded-sm mb-2"
            src={attachmentUrl}
          />
        ) : (
          <div className="w-[94px] h-[94px] bg-gray-100 rounded-sm mb-2 flex items-center justify-center">
            <span className="text-xs text-gray-500 text-center px-2">
              {attachmentName}
            </span>
          </div>
        )}

        <Button
          onPress={() => {
            onFeedbackClick?.(attachmentItem?.id?.toString() || "");
          }}
          size="sm"
          variant="primary"
        >
          Feedback
        </Button>
      </div>

      <div className="flex flex-col gap-2 w-full">
        <div className="flex flex-col justify-center">
          <span className="text-sm">
            <p
              dangerouslySetInnerHTML={{
                __html: mainComment?.message || attachmentName,
              }}
            />
          </span>
        </div>

        {visibleItems.slice(0, INITIAL_VISIBLE_ITEMS).map((item, index) => (
          <div className="flex items-start gap-2" key={item.id}>
            <span
              className={`${item.isResolved ? "bg-utility-success-600 text-text-white" : ""} w-6 h-6 flex items-center justify-center rounded-full border border-[#cfcafc] font-bold text-xs flex-shrink-0`}
            >
              {index + 1}
            </span>
            <span className="text-sm break-words flex-1 min-w-0 max-w-[210px]">
              <div
                className={`transition-all duration-200 ${isExpanded.includes(item.id) ? "" : "line-clamp-1"}`}
                dangerouslySetInnerHTML={{
                  __html: item.message || "No comment",
                }}
              />
              {(item.message?.length || 0) > 50 && (
                <button
                  className="text-utility-brand-600 font-semibold hover:text-primary/80 text-xs mt-1 block cursor-pointer"
                  onClick={() => onClickShowMoreContent(item.id)}
                  type="button"
                >
                  {isExpanded.includes(item.id) ? "Show less" : "Show more"}
                </button>
              )}
            </span>
          </div>
        ))}

        {hasMoreItems && (
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showAll ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="flex flex-col gap-2">
              {hiddenItems.map((item, index) => (
                <div
                  className={`flex items-start gap-2 transform transition-all duration-300 ease-in-out ${
                    showAll
                      ? "translate-y-0 opacity-100"
                      : "translate-y-[-10px] opacity-0"
                  }`}
                  key={item.id}
                  style={{
                    transitionDelay: showAll ? `${index * 50}ms` : "0ms",
                  }}
                >
                  <span
                    className={`${item.isResolved ? "bg-utility-success-600 text-text-white" : ""} w-6 h-6 flex items-center justify-center rounded-full border border-[#cfcafc] font-bold text-xs flex-shrink-0`}
                  >
                    {INITIAL_VISIBLE_ITEMS + index + 1}
                  </span>
                  <span className="text-sm break-words flex-1 min-w-0 max-w-[210px]">
                    <div
                      className={`transition-all duration-200 ${isExpanded.includes(item.id) ? "" : "line-clamp-1"}`}
                      dangerouslySetInnerHTML={{
                        __html: item.message || "No comment",
                      }}
                    />
                    {(item.message?.length || 0) > 50 && (
                      <button
                        className="text-utility-brand-600 font-semibold hover:text-primary/80 text-xs mt-1 block cursor-pointer"
                        onClick={() => onClickShowMoreContent(item.id)}
                        type="button"
                      >
                        {isExpanded.includes(item.id)
                          ? "Show less"
                          : "Show more"}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasMoreItems && (
          <div className="w-full flex justify-center">
            <button
              className="text-utility-brand-600 font-semibold flex items-center gap-1 text-sm mb-2 mt-3 hover:text-utility-brand-700 transition-colors cursor-pointer"
              onClick={() => setShowAll(!showAll)}
              type="button"
            >
              {showAll
                ? "Show less"
                : `Show ${feedbackItems.length - INITIAL_VISIBLE_ITEMS} more`}
              <svg
                className={`inline-block transition-transform duration-300 ease-in-out ${showAll ? "rotate-180" : ""}`}
                fill="none"
                height="20"
                stroke="currentColor"
                viewBox="0 0 24 24"
                width="20"
              >
                <path
                  d="M19 9l-7 7-7-7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </button>
          </div>
        )}

        {feedbackItems.length > 0 && (
          <div className="text-xs text-text-tertiary mt-1 transition-opacity duration-300">
            {showAll
              ? `Showing all ${feedbackItems.length}`
              : `Showing ${Math.min(visibleItems.length, feedbackItems.length)} of ${feedbackItems.length}`}{" "}
            feedback items
          </div>
        )}
      </div>
    </div>
  );
}
